import { useEffect } from "react";
import { Outlet, useLocation } from "react-router-dom";
import { Link, NavLink } from "./navigation.js";
import {
  Bell, ChartNoAxesColumn, Gem, Globe2, House, Spade, UserPlus, UserRound, Users,
} from "lucide-react";
import { Avatar } from "../art/Avatar.js";
import { mobileCopy } from "./mobileCopy.js";
import { useLocale } from "../i18n.js";
import { useSession } from "../net/session.js";
import { usePlayerState } from "./ChannelProvider.js";
import "./shell.css";

export const TABS = [
  { to: "/", key: "home", Icon: House, end: true },
  { to: "/record", key: "record", Icon: ChartNoAxesColumn, end: false },
  { to: "/social", key: "social", Icon: Users, end: false },
  { to: "/me", key: "me", Icon: UserRound, end: false },
] as const;

export function AppShell() {
  const session = useSession();
  const me = session.status === "signedIn" ? session.me : null;
  const { locale } = useLocale();
  const location = useLocation();
  // Both shells used to run their own 15-second invite poll, so a signed-in
  // player made two identical requests a minute before counting the one the
  // Friends screen made on top. The badge now reads the channel store.
  const invites = usePlayerState((state) => state.unread);
  const replayRoute = location.pathname.startsWith("/record/replay/");


  useEffect(() => {
    const tab = TABS.find((item) => item.to === "/"
      ? location.pathname === "/"
      : location.pathname.startsWith(item.to));
    const brand = locale === "zh-CN" ? "掼蛋" : "Guandan";
    document.title = `${tab ? mobileCopy(locale, tab.key) : brand} · ${brand}`;
    const id = window.requestAnimationFrame(() => {
      const heading = document.querySelector<HTMLElement>(".shell-main h1");
      heading?.setAttribute("tabindex", "-1");
      heading?.focus({ preventScroll: true });
    });
    return () => window.cancelAnimationFrame(id);
  }, [location.pathname, locale]);

  return (
    <div className={`app-frame shell${replayRoute ? " shell-replay" : ""}`}>
      <header className="statusbar">
        <Link to="/me" className="statusbar-id">
          {me && <Avatar avatarId={me.user.avatarId} size={36} />}
          <span>
            {me?.user.displayName ?? (locale === "zh-CN" ? "掼蛋" : "Guandan")}
            <small>{me ? `${me.progress.rating} ${locale === "zh-CN" ? "积分" : "rating"}` : ""}</small>
          </span>
        </Link>
        <div className="statusbar-actions">
          <span className="coin-pill"><Gem aria-hidden="true" /><b className="tabular">{me?.progress.coins ?? 0}</b></span>
          <Link className="notify-button" to="/notifications" aria-label={mobileCopy(locale, "notifications")}>
            <Bell aria-hidden="true" />
            {invites > 0 && <b>{invites}</b>}
          </Link>
        </div>
      </header>

      <main className={`shell-main${replayRoute ? " shell-main-replay" : ""}`}>
        <Outlet />
      </main>

      <nav className="bottomnav" aria-label={locale === "zh-CN" ? "主导航" : "Main navigation"}>
        {TABS.map(({ Icon, ...item }) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.end}
            className={({ isActive }) => `navtab${isActive ? " navtab-active" : ""}`}
          >
            <span className="navicon-well" aria-hidden="true"><Icon className="navicon" /></span>
            <span className="navtab-label">{mobileCopy(locale, item.key)}</span>
          </NavLink>
        ))}
      </nav>
    </div>
  );
}

export function PublicReplayShell() {
  const { locale, setLocale } = useLocale();
  return (
    <div className="app-frame public-shell public-replay-shell">
      <header className="public-statusbar">
        <Link to="/" className="public-brand"><Spade aria-hidden="true" />{locale === "zh-CN" ? "掼蛋" : "Guandan"}</Link>
        <span>
          <button
            type="button"
            className="public-language"
            onClick={() => setLocale(locale === "zh-CN" ? "en" : "zh-CN")}
          >
            <Globe2 aria-hidden="true" />{locale === "zh-CN" ? "EN" : "中文"}
          </button>
          <Link className="public-register" to="/"><UserPlus aria-hidden="true" />{locale === "zh-CN" ? "注册 / 开始游戏" : "Join / Play"}</Link>
        </span>
      </header>
      <main><Outlet /></main>
    </div>
  );
}

export function FlowShell() {
  return <div className="flow-shell"><main className="app-frame"><Outlet /></main></div>;
}
